/**
 * Insights — Costi abbonamenti (`/insights/subscriptions`).
 *
 * La pagina di chi paga: quanto costano gli abbonamenti all'azienda e ai dipendenti mese per mese,
 * per tipo, categoria e gruppo; chi si avvicina alla soglia fiscale dei fringe benefit; le trattenute
 * del mese da portare in busta; i budget per gruppo con lo speso e la proiezione.
 *
 * I numeri li calcola il BE con una funzione sola (`SubscriptionCosting`): qui si leggono e basta, e il
 * perimetro è quello degli abbonamenti — un responsabile vede il suo team, l'amministrazione tutto.
 * Le trattenute sono l'eccezione: le legge solo chi vede tutta l'azienda.
 */
import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import {
  ActionIcon, Badge, Button, Group, NumberInput, Progress, Select, SimpleGrid, Stack, Table, Text, Textarea, Tooltip,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { modals } from '@mantine/modals';
import {
  IconAlertTriangle, IconChartPie, IconCoin, IconCurrencyEuro, IconDownload, IconPencil, IconPlus, IconReceipt, IconTrash,
  IconUsers, IconWallet,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useAbility } from '@/app/providers/AbilityProvider';
import { MaeCard, MaeField, MaeFormSection, MaeModal, MaePageHead, MaeShell, MaeSubTabs } from '@/shared/desktop/MaeShell';
import { maeTableStyles } from '@/shared/desktop/MaeShell.utils';
import { StatCard } from '@/shared/components/StatCard';
import { MaeAsync } from '@/shared/components/MaeAsync';
import { MaeAdvice } from '@/shared/components/MaeAdvice';
import { MaeLineChart } from '@/shared/components/MaeLineChart';
import { PALETTE_CHARTS } from '@/shared/components/MaeLineChart.utils';
import { Paginated } from '@/shared/components/DataTable/TablePagination';
import { AnalyticsNav } from '@/features/insights/AnalyticsNav';
import { fetchList } from '@/shared/api/jsonapi/client';
import { saveFile } from '@/shared/lib/fileSave';
import { formatMoney } from '@/shared/lib/format';
import { notifyError, notifySuccess } from '@/shared/lib/notify';
import type { Denormalized } from '@/shared/api/jsonapi/parser';
import {
  subscriptionErrorMessage, useBudgetStatus, useCreateBudget, useDeleteBudget, useFringeBenefit,
  usePayroll, useSubscriptionBudgets, useSubscriptionReport, useUpdateBudget,
} from '@/features/subscriptions/api';
import {
  MONTH_LABELS, categoryLabel, payrollCsv, type BudgetStatus, type SubscriptionBudgetRow,
} from '@/features/subscriptions/subscriptions';

export const Route = createFileRoute('/_app/insights/subscriptions')({ component: SubscriptionInsightsPage });

type Section = 'costi' | 'fringe' | 'trattenute' | 'budget';

interface GroupLite extends Denormalized { type: 'erp-business-groups'; name?: string | null }

function SubscriptionInsightsPage() {
  const ability = useAbility();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [section, setSection] = useState<Section>('costi');
  const canPayroll = ability.can('readAll', 'ErpEmployeeSubscription') || ability.can('manage', 'all');
  const canBudget = ability.can('create', 'ErpSubscriptionBudget');

  const reportQ = useSubscriptionReport(year);
  const fringeQ = useFringeBenefit(year);
  const payrollQ = usePayroll(year, month, canPayroll && section === 'trattenute');
  const budgetsQ = useBudgetStatus(year);

  const report = reportQ.data && reportQ.data.totals ? reportQ.data : undefined;
  const years = Array.from({ length: 4 }, (_, i) => String(now.getFullYear() - 2 + i));
  const chartData = useMemo(() => (report?.months ?? []).map((m) => ({
    month: MONTH_LABELS[m.month - 1], Company: m.company, Employees: m.employee,
  })), [report]);

  const sections = [
    { value: 'costi', label: 'Costi' },
    { value: 'fringe', label: 'Fringe benefit', count: fringeQ.data ? fringeQ.data.over + fringeQ.data.near : undefined },
    ...(canPayroll ? [{ value: 'trattenute', label: 'Trattenute' }] : []),
    { value: 'budget', label: 'Budget', count: budgetsQ.data?.filter((b) => b.level !== 'ok').length || undefined },
  ];

  return (
    <MaeShell>
      <AnalyticsNav active="/insights/subscriptions" />
      <MaePageHead
        title="Costi abbonamenti"
        sub="Quanto costano gli abbonamenti all'azienda e ai dipendenti, chi si avvicina alla soglia fiscale, cosa va in busta paga"
        icon={<IconChartPie size={23} />}
        actions={<Select w={110} data={years} value={String(year)} onChange={(v) => v && setYear(Number(v))} allowDeselect={false} aria-label="Anno" />}
      />

      <SimpleGrid cols={{ base: 1, sm: 4 }} spacing="md" mb="lg">
        <StatCard label={`Costo azienda ${year}`} value={formatMoney(report?.totals.company ?? 0)} icon={IconCurrencyEuro} color="dark" />
        <StatCard label="Di cui maturato a oggi" value={formatMoney(report?.totals.ytdCompany ?? 0)} icon={IconWallet} color="gray" />
        <StatCard label="A carico dei dipendenti" value={formatMoney(report?.totals.employee ?? 0)} icon={IconUsers} color="gray" />
        <StatCard label="Righe sopra listino" value={report?.totals.aboveListRows ?? 0} icon={IconAlertTriangle} color={(report?.totals.aboveListRows ?? 0) > 0 ? 'orange' : 'gray'} />
      </SimpleGrid>

      <MaeSubTabs value={section} onChange={(v) => setSection(v as Section)} tabs={sections} />

      {section === 'costi' && (
        <MaeAsync
          query={reportQ}
          loading="Calcolo dei costi…"
          empty={(report?.totals.employees ?? 0) === 0 && {
            icon: IconChartPie,
            title: `Nessun abbonamento con costi nel ${year}`,
            description: 'Il report somma gli abbonamenti attivi mese per mese. Quando HR ne assegna uno con un costo compare qui, diviso fra azienda e dipendente.',
          }}
        >
          {report && (
            <Stack gap={16}>
              <MaeCard>
                <Text fw={700} size="sm" mb={8}>Andamento mensile</Text>
                <MaeLineChart
                  data={chartData}
                  /* `name` è la chiave del dato, `label` la scritta: il lotto della traduzione aveva
                     rinominato le chiavi di `chartData` e non queste, e il grafico restava vuoto. */
                  dataKey="month"
                  series={[
                    { name: 'Company', label: 'Azienda', color: PALETTE_CHARTS.cost },
                    { name: 'Employees', label: 'Dipendenti', color: PALETTE_CHARTS.actual },
                  ]}
                  h={220}
                  unit=" €"
                />
              </MaeCard>

              {report.totals.aboveListRows > 0 && (
                <MaeAdvice tone="warning" title={`${report.totals.aboveListRows} ${report.totals.aboveListRows === 1 ? 'abbonamento costa' : 'abbonamenti costano'} più del listino del tipo`}
                  selfRemedy="Sono i contratti da rinegoziare, o il listino da aggiornare: li trovi per tipo nella tabella qui sotto e con il badge «sopra listino» nelle liste.">
                  Il listino è il costo di riferimento scritto sul tipo; la riga paga di più.
                </MaeAdvice>
              )}

              <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
                <MaeCard padding={0}>
                  <Text fw={700} size="sm" p={14} pb={6}>Per tipo</Text>
                  <Table.ScrollContainer minWidth={520}>
                    <Table striped verticalSpacing="xs" styles={maeTableStyles}>
                      <Table.Thead>
                        <Table.Tr><Table.Th>Tipo</Table.Th><Table.Th>Righe</Table.Th><Table.Th>Azienda</Table.Th><Table.Th>Dipendenti</Table.Th><Table.Th>Documentato</Table.Th></Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {report.byType.map((t) => (
                          <Table.Tr key={t.typeId ?? 'none'}>
                            <Table.Td>
                              <Text size="sm" fw={600}>{t.name}</Text>
                              <Text size="xs" c="dimmed">{categoryLabel(t.category)}{t.aboveList > 0 ? ` · ${t.aboveList} sopra listino` : ''}</Text>
                            </Table.Td>
                            <Table.Td><Text size="sm">{t.activeRows} / {t.rows}</Text></Table.Td>
                            <Table.Td><Text size="sm" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatMoney(t.company)}</Text></Table.Td>
                            <Table.Td><Text size="sm" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatMoney(t.employee)}</Text></Table.Td>
                            <Table.Td>
                              <Tooltip label="Somma degli importi di ricevute e fatture allegate alle righe di questo tipo">
                                <Text size="sm" c={t.documented > 0 ? undefined : 'dimmed'} style={{ fontVariantNumeric: 'tabular-nums' }}>{t.documented > 0 ? formatMoney(t.documented) : '—'}</Text>
                              </Tooltip>
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </Table.ScrollContainer>
                </MaeCard>

                <Stack gap={16}>
                  <MaeCard padding={0}>
                    <Text fw={700} size="sm" p={14} pb={6}>Per categoria</Text>
                    <Table striped verticalSpacing="xs" styles={maeTableStyles}>
                      <Table.Tbody>
                        {report.byCategory.map((c) => (
                          <Table.Tr key={c.category}>
                            <Table.Td><Text size="sm">{categoryLabel(c.category)}</Text></Table.Td>
                            <Table.Td><Text size="sm" c="dimmed">{c.rows} righe</Text></Table.Td>
                            <Table.Td><Text size="sm" fw={600} style={{ fontVariantNumeric: 'tabular-nums' }}>{formatMoney(c.company)}</Text></Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </MaeCard>
                  <MaeCard padding={0}>
                    <Text fw={700} size="sm" p={14} pb={6}>Per gruppo aziendale</Text>
                    <Table striped verticalSpacing="xs" styles={maeTableStyles}>
                      <Table.Tbody>
                        {report.byGroup.map((g) => (
                          <Table.Tr key={g.groupId ?? 'none'}>
                            <Table.Td><Text size="sm">{g.name}</Text></Table.Td>
                            <Table.Td><Text size="sm" c="dimmed">{g.employees} {g.employees === 1 ? 'persona' : 'persone'}</Text></Table.Td>
                            <Table.Td><Text size="sm" fw={600} style={{ fontVariantNumeric: 'tabular-nums' }}>{formatMoney(g.company)}</Text></Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </MaeCard>
                </Stack>
              </SimpleGrid>
            </Stack>
          )}
        </MaeAsync>
      )}

      {section === 'fringe' && <FringeSection year={year} query={fringeQ} />}
      {section === 'trattenute' && canPayroll && (
        <PayrollSection year={year} month={month} onMonth={setMonth} query={payrollQ} />
      )}
      {section === 'budget' && <BudgetSection year={year} statusQ={budgetsQ} canWrite={canBudget} />}
    </MaeShell>
  );
}

/* ------------------------------------------------------------------ fringe benefit */

function FringeSection({ year, query }: { year: number; query: ReturnType<typeof useFringeBenefit> }) {
  const data = query.data;
  const rows = useMemo(() => [...(data?.employees ?? [])].sort((a, b) => b.ratio - a.ratio), [data]);
  return (
    <MaeAsync
      query={query}
      loading="Calcolo delle quote per dipendente…"
      empty={rows.length === 0 && {
        icon: IconCoin,
        title: `Nessuna quota azienda nel ${year}`,
        description: 'La soglia fringe benefit si controlla sulla quota a carico dell\'azienda: quando ci sono abbonamenti con quel dato la lista compare qui, ordinata da chi è più vicino alla soglia.',
      }}
    >
      {data && (
        <Stack gap={16}>
          <MaeAdvice tone={data.over > 0 ? 'danger' : data.near > 0 ? 'warning' : 'info'}
            title={data.over > 0 ? `${data.over} ${data.over === 1 ? 'dipendente ha superato' : 'dipendenti hanno superato'} la soglia di ${formatMoney(data.threshold)}` : data.near > 0 ? `${data.near} ${data.near === 1 ? 'dipendente si avvicina' : 'dipendenti si avvicinano'} alla soglia di ${formatMoney(data.threshold)}` : `Nessuno vicino alla soglia di ${formatMoney(data.threshold)}`}
            selfRemedy="La soglia annua e la percentuale di preavviso si cambiano in Organizzazione → Regole di lavoro → Abbonamenti, nella scheda «Fringe benefit».">
            Oltre la soglia annua la quota a carico dell'azienda diventa reddito imponibile per il dipendente: chi fa le paghe deve saperlo prima della busta.
          </MaeAdvice>
          <MaeCard padding={0}>
            <Paginated rows={rows} pageSize={20} label="dipendenti" barPad={16}>
              {(page) => (
                <Table striped verticalSpacing="sm" styles={maeTableStyles}>
                  <Table.Thead>
                    <Table.Tr><Table.Th>Dipendente</Table.Th><Table.Th>Abbonamenti</Table.Th><Table.Th>Quota azienda {year}</Table.Th><Table.Th w={260}>Rispetto alla soglia</Table.Th></Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {page.map((e) => (
                      <Table.Tr key={e.employeeId}>
                        <Table.Td><Text size="sm" fw={600}>{e.name}</Text>{e.employeeCode && <Text size="xs" c="dimmed">{e.employeeCode}</Text>}</Table.Td>
                        <Table.Td><Text size="sm">{e.rows}</Text></Table.Td>
                        <Table.Td><Text size="sm" fw={600} style={{ fontVariantNumeric: 'tabular-nums' }}>{formatMoney(e.company)}</Text></Table.Td>
                        <Table.Td>
                          <Group gap={8} wrap="nowrap">
                            <Progress value={Math.min(100, e.ratio * 100)} color={e.level === 'over' ? 'red' : e.level === 'near' ? 'orange' : 'teal'} style={{ flex: 1 }} size="sm" />
                            <Badge size="sm" variant="light" color={e.level === 'over' ? 'red' : e.level === 'near' ? 'orange' : 'gray'}>{Math.round(e.ratio * 100)}%</Badge>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Paginated>
          </MaeCard>
        </Stack>
      )}
    </MaeAsync>
  );
}

/* ------------------------------------------------------------------ trattenute */

function PayrollSection({ year, month, onMonth, query }: {
  year: number; month: number; onMonth: (m: number) => void; query: ReturnType<typeof usePayroll>;
}) {
  const data = query.data;
  async function download() {
    if (!data) return;
    const outcome = await saveFile(`trattenute-abbonamenti-${year}-${String(month).padStart(2, '0')}.csv`, payrollCsv(data), { mime: 'text/csv;charset=utf-8' });
    if ('messaggio' in outcome && outcome.messaggio) notifyError(outcome.messaggio); else notifySuccess('CSV pronto');
  }
  return (
    <Stack gap={16}>
      <Group justify="space-between" wrap="wrap">
        <Group gap={8}>
          <Select w={140} data={MONTH_LABELS.map((l, i) => ({ value: String(i + 1), label: l }))} value={String(month)} onChange={(v) => v && onMonth(Number(v))} allowDeselect={false} aria-label="Mese" />
          <Text size="sm" c="dimmed">La quota a carico dei dipendenti negli abbonamenti attivi nel mese: è ciò che va in busta come trattenuta.</Text>
        </Group>
        <Button leftSection={<IconDownload size={16} />} variant="light" onClick={download} disabled={!data || data.rows.length === 0}>Scarica CSV</Button>
      </Group>
      <MaeAsync
        query={query}
        loading="Calcolo delle trattenute…"
        empty={(data?.rows.length ?? 0) === 0 && {
          icon: IconReceipt,
          title: `Nessuna trattenuta per ${MONTH_LABELS[month - 1]} ${year}`,
          description: 'Compaiono qui i dipendenti che in questo mese hanno abbonamenti attivi con una quota a loro carico. Se la quota azienda non è specificata, il costo è tutto dell\'azienda e non si trattiene nulla.',
        }}
      >
        {data && (
          <MaeCard padding={0}>
            <Table striped verticalSpacing="sm" styles={maeTableStyles}>
              <Table.Thead>
                <Table.Tr><Table.Th>Dipendente</Table.Th><Table.Th>Voci</Table.Th><Table.Th>Trattenuta</Table.Th><Table.Th>Quota azienda</Table.Th></Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.rows.map((r) => (
                  <Table.Tr key={r.employeeId}>
                    <Table.Td><Text size="sm" fw={600}>{r.name}</Text>{r.employeeCode && <Text size="xs" c="dimmed">{r.employeeCode}</Text>}</Table.Td>
                    <Table.Td><Text size="xs" c="dimmed">{r.items.map((i) => i.typeName).join(', ')}</Text></Table.Td>
                    <Table.Td><Text size="sm" fw={700} style={{ fontVariantNumeric: 'tabular-nums' }}>{formatMoney(r.employeeShare)}</Text></Table.Td>
                    <Table.Td><Text size="sm" c="dimmed" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatMoney(r.companyShare)}</Text></Table.Td>
                  </Table.Tr>
                ))}
                <Table.Tr>
                  <Table.Td><Text size="sm" fw={700}>Totale</Text></Table.Td>
                  <Table.Td><Text size="xs" c="dimmed">{data.totals.employees} dipendenti</Text></Table.Td>
                  <Table.Td><Text size="sm" fw={700} style={{ fontVariantNumeric: 'tabular-nums' }}>{formatMoney(data.totals.employeeShare)}</Text></Table.Td>
                  <Table.Td><Text size="sm" fw={700} style={{ fontVariantNumeric: 'tabular-nums' }}>{formatMoney(data.totals.companyShare)}</Text></Table.Td>
                </Table.Tr>
              </Table.Tbody>
            </Table>
          </MaeCard>
        )}
      </MaeAsync>
    </Stack>
  );
}

/* ------------------------------------------------------------------ budget */

interface BudgetForm { businessGroupId: string | null; amount: number | string; notes: string }

function BudgetSection({ year, statusQ, canWrite }: { year: number; statusQ: ReturnType<typeof useBudgetStatus>; canWrite: boolean }) {
  const budgetsQ = useSubscriptionBudgets(year);
  const groupsQ = useQuery({
    queryKey: ['subscriptions', 'budget-groups'],
    queryFn: () => fetchList<GroupLite>('erp-business-groups', { sort: ['name'], page: { size: 100 } }),
    enabled: canWrite,
    staleTime: 5 * 60_000,
  });
  const create = useCreateBudget();
  const update = useUpdateBudget();
  const remove = useDeleteBudget();
  const [editing, setEditing] = useState<SubscriptionBudgetRow | null>(null);
  const [open, setOpen] = useState(false);

  const form = useForm<BudgetForm>({
    initialValues: { businessGroupId: null, amount: '', notes: '' },
    validate: { amount: (v) => (typeof v === 'number' && v > 0 ? null : 'Indica un importo maggiore di zero') },
  });

  function apri(b: SubscriptionBudgetRow | null) {
    setEditing(b);
    const values: BudgetForm = { businessGroupId: b?.businessGroup?.id ?? null, amount: typeof b?.amount === 'number' ? b.amount : '', notes: b?.notes ?? '' };
    form.setValues(values); form.resetDirty(values);
    setOpen(true);
  }

  async function submit(values: BudgetForm) {
    const attributes = { year, amount: values.amount, currency: 'EUR', notes: values.notes.trim() || null };
    const relationships = { businessGroup: values.businessGroupId ? { data: { type: 'erp-business-groups', id: values.businessGroupId } } : { data: null } };
    try {
      if (editing) { await update.mutateAsync({ id: editing.id, attributes, relationships }); notifySuccess('Budget aggiornato'); }
      else { await create.mutateAsync({ attributes, relationships }); notifySuccess('Budget creato'); }
      setOpen(false);
      void statusQ.refetch();
    } catch (e) { notifyError(subscriptionErrorMessage(e)); }
  }

  function elimina(b: SubscriptionBudgetRow) {
    modals.openConfirmModal({
      title: 'Elimina budget',
      children: <Text size="sm">Elimini il budget {year} per <strong>{b.businessGroup?.name ?? 'tutta l\'azienda'}</strong>? Le righe assegnate non cambiano: sparisce solo il tetto di controllo.</Text>,
      labels: { confirm: 'Elimina', cancel: 'Annulla' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try { await remove.mutateAsync(b.id); notifySuccess('Budget eliminato'); void statusQ.refetch(); }
        catch (e) { notifyError(subscriptionErrorMessage(e)); }
      },
    });
  }

  const status = statusQ.data ?? [];
  const byId = useMemo(() => new Map((budgetsQ.data?.data ?? []).map((b) => [b.id, b])), [budgetsQ.data]);
  const groupOptions = [{ value: '', label: 'Tutta l\'azienda' }, ...(groupsQ.data?.data ?? []).map((g) => ({ value: g.id, label: g.name ?? g.id }))];

  return (
    <Stack gap={16}>
      <Group justify="space-between" wrap="wrap">
        <Text size="sm" c="dimmed" style={{ maxWidth: 640 }}>
          Un tetto annuo di quota azienda per gruppo, o per tutta l'azienda. Non blocca le assegnazioni: dice quanto resta e quando la proiezione a fine anno lo supera.
        </Text>
        {canWrite && <Button leftSection={<IconPlus size={16} />} onClick={() => apri(null)}>Nuovo budget</Button>}
      </Group>
      <MaeAsync
        query={[statusQ, budgetsQ]}
        loading="Caricamento dei budget…"
        empty={status.length === 0 && {
          icon: IconWallet,
          title: `Nessun budget per il ${year}`,
          description: 'Un budget è un tetto di quota azienda per gruppo o per tutta l\'azienda: il report confronta lo speso fin qui e la proiezione a fine anno, e avvisa quando si supera.',
          ...(canWrite ? { action: { label: 'Nuovo budget', onClick: () => apri(null) } } : {}),
        }}
      >
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
          {status.map((b: BudgetStatus) => {
            const row = byId.get(b.budgetId);
            const color = b.level === 'over' ? 'red' : b.level === 'near' ? 'orange' : 'teal';
            return (
              <MaeCard key={b.budgetId}>
                <Group justify="space-between" align="flex-start" wrap="nowrap">
                  <div>
                    <Text fw={700} size="sm">{b.name}</Text>
                    <Text size="xs" c="dimmed">{formatMoney(b.spentYtd, b.currency)} maturati a oggi · proiezione {formatMoney(b.projected, b.currency)} su {formatMoney(b.amount, b.currency)}</Text>
                    {b.notes && <Text size="xs" c="dimmed" mt={2}>{b.notes}</Text>}
                  </div>
                  <Group gap={4} wrap="nowrap">
                    <Badge size="sm" variant="light" color={color}>{Math.round(b.ratioProjected * 100)}%</Badge>
                    {canWrite && row && (
                      <>
                        <ActionIcon variant="subtle" color="gray" aria-label="Modifica budget" onClick={() => apri(row)}><IconPencil size={15} /></ActionIcon>
                        <ActionIcon variant="subtle" color="red" aria-label="Elimina budget" onClick={() => elimina(row)}><IconTrash size={15} /></ActionIcon>
                      </>
                    )}
                  </Group>
                </Group>
                <Progress.Root size="lg" mt={12}>
                  <Progress.Section value={Math.min(100, b.ratioYtd * 100)} color={color} />
                  <Progress.Section value={Math.max(0, Math.min(100, b.ratioProjected * 100) - Math.min(100, b.ratioYtd * 100))} color={color} style={{ opacity: 0.35 }} />
                </Progress.Root>
                {b.level === 'over' && (
                  <Text size="xs" c="red" mt={6}>La proiezione supera il tetto di {formatMoney(b.projected - b.amount, b.currency)}: qualche abbonamento va chiuso o il budget rivisto.</Text>
                )}
              </MaeCard>
            );
          })}
        </SimpleGrid>
      </MaeAsync>

      <MaeModal opened={open} onClose={() => setOpen(false)} title={editing ? 'Modifica budget' : `Nuovo budget ${year}`} icon={<IconWallet size={17} />}
        footer={<Button onClick={() => form.onSubmit(submit)()} loading={create.isPending || update.isPending}>{editing ? 'Salva' : 'Crea'}</Button>}>
        <MaeFormSection title="Perimetro e importo" sub="Un solo budget per gruppo e anno. Il budget aziendale conta tutti i dipendenti, anche quelli senza gruppo.">
          <MaeField label="Perimetro">
            <Select data={groupOptions} value={form.values.businessGroupId ?? ''} onChange={(v) => form.setFieldValue('businessGroupId', v || null)} allowDeselect={false} disabled={!!editing} />
          </MaeField>
          <MaeField label={`Quota azienda ammessa nel ${year}`} required>
            <NumberInput min={0} step={100} decimalScale={2} leftSection={<Text size="xs" c="dimmed" pl={4} style={{ fontVariantNumeric: 'tabular-nums' }}>€</Text>} {...form.getInputProps('amount')} />
          </MaeField>
          <MaeField label="Note">
            <Textarea autosize minRows={2} {...form.getInputProps('notes')} />
          </MaeField>
        </MaeFormSection>
      </MaeModal>
    </Stack>
  );
}
